import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { format } from "date-fns";

import * as config from "@/config";

import { type DailyWordCountData, type DateRange } from "../types";
import { useHermesApiToken } from "./use-hermes-api-token";

/**
 *
 * Hook to fetch the Activity Report Insights data from Hermes.
 *
 * No data (null) is returned if the Hermes token is not provided yet.
 * @param tenant
 * @param assessmentId
 * @param apiToken Hermes token given by Pantheon to make Hermes API calls.
 *
 * @param studentIds The student IDs to apply to the activity report.
 * @returns An `InsightsActivityReport` object on success.
 */
export function useWordCountActivity(
  assessmentId: string,
  workId: string,
  studentIds: string[],
  dateRange?: DateRange
): UseQueryResult<DailyWordCountData | null> {
  const apiToken = useHermesApiToken({ assessmentId, workId });
  return useQuery(
    [`insights/word_counts`, studentIds, dateRange],
    async () => {
      let hermesUrl = `${config.HERMESV2_ENDPOINT}/api/insights/word_counts`;
      if (dateRange && dateRange[0] && dateRange[1]) {
        // convert date in YYYY-MM-DD format
        const startDate = format(dateRange[0], "yyyy-MM-dd");
        const endDate = format(dateRange[1], "yyyy-MM-dd");
        hermesUrl += `?start_date=${startDate}&end_date=${endDate}`;
      }
      try {
        const response = await fetch(hermesUrl, {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${apiToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_ids: studentIds,
            group_ids: [],
          }),
        });

        const data: DailyWordCountData = await response.json();
        return data;
      } catch (e) {
        console.error("failed to fetch Activity Report from Hermes", e);
        return null;
      }
    },
    {
      cacheTime: 100, // No cache, as the default is 5 minutes.
      // Refetch the data only on refresh: decrease database load.
      refetchInterval: false,
      useErrorBoundary: true,
      enabled: !!apiToken,
    }
  );
}
